import { router } from 'expo-router';
import { useCallback, useMemo } from 'react';

import { useCartValues } from '@fhs/cart/src/state/cart-store';
import useCartTotal from '@fhs/cart/src/state/cart-store/pricing-cart/use-cart-total';
import { Schema, client, queryClient, useQuery } from '@fhs/client';
import {
  ILoyaltyQuest,
  ILoyaltyQuestUi,
  ILoyaltyQuestsQuery,
  ILoyaltyQuestsQueryVariables,
  ILoyaltyQuestsUiQuery,
  ILoyaltyQuestsUiQueryVariables,
  ILoyaltyRewardTier,
  ILoyaltyRewardTiersQuery,
  ILoyaltyRewardTiersQueryVariables,
  ILoyaltyUserEstimatedPointsEarnedQuery,
  ILoyaltyUserEstimatedPointsEarnedQueryVariables,
  ILoyaltyUserTransactionsQuery,
  ILoyaltyUserTransactionsQueryVariables,
  ITransactionFragment,
  LoyaltyQuestsDocument,
  LoyaltyQuestsUiDocument,
  LoyaltyRewardTiersDocument,
  LoyaltyServiceMode,
  LoyaltyUserEstimatedPointsEarnedDocument,
  LoyaltyUserTransactionsDocument,
} from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import {
  selectors,
  useAppSelector,
} from '@fhs-legacy/frontend/src/state/global-state';
import { parseEntry, parseOffers } from '@fhs-legacy/frontend/src/state/global-state/models/loyalty/offers/offers.utils';
import { client as legacyClient } from '@fhs-legacy/frontend/src/state/graphql/client';
import { usePaymentContext } from '@fhs-legacy/frontend/src/state/payment';
import { frontendToBackendCartEntryMap } from '@fhs-legacy/frontend/src/utils/cart';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';
import { mapPaymentMethodToLoyaltyPaymentMethods } from '@fhs-legacy/frontend/src/utils/payment';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';
import { ICartEntry } from '@rbi-ctg/menu';

import { useApplyOffer } from '../hooks/use-apply-offer';
import { useLoyaltyLegacyStates } from '../hooks/use-loyalty-legacy-states';
import { useRemoveOffer } from '../hooks/use-remove-offer';
import { toLoyaltyPromotionInput } from '../utils/helpers';

export const loyaltyIncentivesQueryKeys = {
  all: ['loyaltyIncentives'] as const,
  params: ({
    storeId,
    serviceMode,
    loyaltyId,
  }: {
    storeId: string;
    serviceMode: string;
    loyaltyId?: string;
  }) => [...loyaltyIncentivesQueryKeys.all, storeId, serviceMode, loyaltyId] as const,
};

export type LoyaltyIncentiveOffer = Schema['OfferListItem']['type'] & {
  applyOffer: () => Promise<void>;
  removeOffer: () => void;
  seeDetails: () => void;
  isApplied: boolean;
};

export const useLoyaltyIncentives = () => {
  const { store, serviceMode, loyaltyUserReady, loyaltyId, selectedOffer } =
    useLoyaltyLegacyStates();

  const { applyOffer } = useApplyOffer();
  const { removeOffer } = useRemoveOffer();

  const { data, ...props } = useQuery({
    enabled: loyaltyUserReady && !!store.number && !!serviceMode,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    queryKey: loyaltyIncentivesQueryKeys.params({
      serviceMode: serviceMode ?? '',
      storeId: store.number ?? '',
      loyaltyId,
    }),
    meta: { persist: false },
    queryFn: async () => {
      const response = await client.queries.getLoyaltyIncentives({
        storeNumber: store.number ?? '',
        serviceMode: serviceMode ?? '',
        loyaltyId,
      });
      if (response.errors) {
        throw response.errors;
      }

      return response.data;
    },
  });

  const prepareOfferData = useCallback(
    (offers: Schema['OfferListItem']['type'][]) => {
      return offers.map<LoyaltyIncentiveOffer>(offer => ({
        ...offer,
        isApplied: selectedOffer?._id === offer.id,
        applyOffer: async () => {
          await applyOffer(offer);
        },
        removeOffer: () => {
          removeOffer();
        },
        seeDetails: () => {
          router.navigate(routes.offersV2Details + `/${offer.id}`);
        },
      }));
    },
    [selectedOffer, applyOffer]
  );

  const loyaltyIncentivesData = useMemo(
    () => ({
      ...data,
      offers: prepareOfferData(data?.offers ?? []),
    }),
    [data, prepareOfferData]
  );

  return {
    ...props,
    data: loyaltyIncentivesData,
  };
};

export const useOfferIncentive = (offerId: string) => {
  const { data, ...props } = useLoyaltyIncentives();

  const currentOffer = useMemo(
    () => data?.offers?.find(offer => offer && offer.id === offerId),
    [data?.offers, offerId]
  );
  return {
    ...props,
    data: currentOffer,
  };
};

export const offerDetailUiQueryKeys = {
  all: ['offerDetailUi'] as const,
  params: ({
    storeId,
    serviceMode,
    loyaltyId,
    offerId,
    cartEntriesV1,
  }: {
    storeId: string;
    serviceMode: string;
    loyaltyId?: string;
    offerId: string;
    cartEntriesV1: any;
  }) =>
    [
      ...offerDetailUiQueryKeys.all,
      offerId,
      storeId,
      serviceMode,
      loyaltyId,
      cartEntriesV1,
    ] as const,
};

export const useOfferDetailUi = (offerId: string) => {
  const { store, serviceMode, loyaltyUserReady, loyaltyId } = useLoyaltyLegacyStates();
  const { cartEntries } = useCartValues();
  const backendCartEntries = useMemo(
    () => (cartEntries ? cartEntries.map(frontendToBackendCartEntryMap) : []),
    [cartEntries]
  );
  // this a temp cartEntriesV1, we need to include from state
  const cartEntriesV1 = useMemo(() => JSON.stringify(backendCartEntries), [backendCartEntries]);

  return useQuery({
    enabled: loyaltyUserReady && !!store.number && !!serviceMode,
    refetchOnWindowFocus: false,
    queryKey: offerDetailUiQueryKeys.params({
      cartEntriesV1,
      serviceMode: serviceMode ?? '',
      storeId: store.number ?? '',
      loyaltyId,
      offerId,
    }),
    queryFn: async () => {
      const response = await client.queries.getOfferDetailUi({
        storeNumber: store.number ?? '',
        serviceMode: serviceMode ?? '',
        loyaltyId,
        offerId,
        cartEntriesV1,
      });
      if (response.errors) {
        throw response.errors;
      }

      return response.data;
    },
  });
};

export const challengesQueryKey = ['challenges'];

export const challengesUiQueryKeys = {
  all: [...challengesQueryKey, 'challengesUi'] as const,
  loyaltyUser: ({
    loyaltyId,
    isChallengeUiEnabled = true,
  }: {
    loyaltyId?: string;
    isChallengeUiEnabled?: boolean;
  }) => [...challengesUiQueryKeys.all, loyaltyId, isChallengeUiEnabled] as const,
};

export const useChallengesUi = () => {
  const { loyaltyId } = useLoyaltyLegacyStates();
  const isChallengeUiEnabled = useFlag('enable-challenge-ui');

  return useQuery({
    enabled: Boolean(isChallengeUiEnabled && !!loyaltyId),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryKey: challengesUiQueryKeys.loyaltyUser({
      loyaltyId,
      isChallengeUiEnabled,
    }),
    queryFn: async () => {
      const response = await legacyClient.query<
        ILoyaltyQuestsUiQuery,
        ILoyaltyQuestsUiQueryVariables
      >({
        query: LoyaltyQuestsUiDocument,
        variables: { loyaltyId },
        fetchPolicy: 'no-cache',
      });
      if (response.errors) {
        throw response.errors;
      }
      return response.data.loyaltyQuestsUIV2 as ILoyaltyQuestUi[];
    },
  });
};

export const useChallenge = (challengeId: string) => {
  const { data, ...props } = useChallengesUi();

  const currentChallenge = useMemo(
    () => data?.find(challenge => challenge && challenge.id === challengeId),
    [data, challengeId]
  );
  return {
    ...props,
    data: currentChallenge,
  };
};

const evaluateChallenges = async (cartEntries: ICartEntry[], loyaltyId: string) => {
  const parsedEntries = cartEntries.reduce(parseEntry(), []);
  const response = await legacyClient.query<ILoyaltyQuestsQuery, ILoyaltyQuestsQueryVariables>({
    query: LoyaltyQuestsDocument,
    variables: { loyaltyId, cartEntries: parsedEntries },
    fetchPolicy: 'no-cache',
  });
  if (response.errors) {
    throw response.errors;
  }
  return response.data.loyaltyQuestsV2 as ILoyaltyQuest[];
};

export const evaluateChallengesQueryKeys = {
  all: [...challengesQueryKey, 'evaluateChallenges'] as const,
  loyaltyUser: ({
    loyaltyId,
    isChallengeUiEnabled = true,
  }: {
    loyaltyId?: string;
    isChallengeUiEnabled?: boolean;
  }) => [...evaluateChallengesQueryKeys.all, loyaltyId, isChallengeUiEnabled] as const,
  cart: ({
    loyaltyId,
    isChallengeUiEnabled = true,
    cartEntries,
  }: {
    loyaltyId?: string;
    isChallengeUiEnabled?: boolean;
    cartEntries?: ICartEntry[];
  }) =>
    [
      ...evaluateChallengesQueryKeys.loyaltyUser({ loyaltyId, isChallengeUiEnabled }),
      cartEntries,
    ] as const,
};

export const useEvaluateChallenges = () => {
  const { loyaltyId } = useLoyaltyLegacyStates();
  const { cartEntries } = useCartValues();

  const isChallengeUiEnabled = useFlag('enable-challenge-ui');

  return useQuery({
    enabled: isChallengeUiEnabled && !!loyaltyId,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    placeholderData: previousData => previousData ?? [],
    queryKey: evaluateChallengesQueryKeys.cart({
      loyaltyId,
      isChallengeUiEnabled,
      cartEntries,
    }),
    queryFn: async () => await evaluateChallenges(cartEntries, loyaltyId),
  });
};

export const prefetchEvaluateChallenges = async (cartEntries: ICartEntry[], loyaltyId: string) => {
  return queryClient.prefetchQuery({
    queryKey: evaluateChallengesQueryKeys.cart({
      loyaltyId,
      cartEntries,
    }),
    queryFn: async () => await evaluateChallenges(cartEntries, loyaltyId),
  });
};

export const loyaltyUserTransactionsQueryKeys = {
  all: ['loyaltyUserTransactions'] as const,
  loyaltyUser: ({ loyaltyId }: { loyaltyId?: string }) =>
    [...loyaltyUserTransactionsQueryKeys.all, loyaltyId] as const,
};

export const useLoyaltyUserTransactions = () => {
  const { loyaltyId } = useLoyaltyLegacyStates();

  return useQuery({
    enabled: !!loyaltyId,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    queryKey: loyaltyUserTransactionsQueryKeys.loyaltyUser({
      loyaltyId,
    }),
    queryFn: async () => {
      const response = await legacyClient.query<
        ILoyaltyUserTransactionsQuery,
        ILoyaltyUserTransactionsQueryVariables
      >({
        query: LoyaltyUserTransactionsDocument,
        variables: { loyaltyId },
        fetchPolicy: 'no-cache',
      });
      if (response.errors) {
        throw response.errors;
      }

      return (response.data?.loyaltyUserV2?.transactions ?? []) as ITransactionFragment[];
    },
  });
};

export const rewardsTiersQueryKey = ['rewardTiers'];

export const rewardsTiersQueryKeys = {
  all: [...rewardsTiersQueryKey, 'getRewardsTiers'] as const,
  loyaltyUser: ({ loyaltyId }: { loyaltyId?: string }) =>
    [...rewardsTiersQueryKeys.all, loyaltyId] as const,
};

export const useRewardTiers = () => {
  const enableRewardsTiers = useFlag('enable-rewards-tiers');
  const { loyaltyId, loyaltyUserReady } = useLoyaltyLegacyStates();

  return useQuery({
    enabled: enableRewardsTiers && loyaltyUserReady,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    queryKey: rewardsTiersQueryKeys.loyaltyUser({
      loyaltyId,
    }),
    queryFn: async () => {
      const response = await legacyClient.query<
        ILoyaltyRewardTiersQuery,
        ILoyaltyRewardTiersQueryVariables
      >({
        query: LoyaltyRewardTiersDocument,
        variables: { loyaltyId },
        fetchPolicy: 'no-cache',
      });
      if (response.errors) {
        throw response.errors;
      }
      return response.data.loyaltyRewardTiersV2 as ILoyaltyRewardTier[];
    },
  });
};

export const estimatedPointsEarnedQueryKeys = {
  all: ['estimatedPointsEarned'] as const,
  params: ({
    loyaltyId,
    entryIds,
    subtotalAmount,
    offerIds,
    paymentMethod,
    serviceMode,
    storeId,
  }: {
    loyaltyId?: string;
    entryIds: string;
    subtotalAmount: number;
    offerIds: string;
    paymentMethod?: string;
    serviceMode?: string;
    storeId?: string;
  }) =>
    [
      ...estimatedPointsEarnedQueryKeys.all,
      loyaltyId,
      entryIds,
      subtotalAmount,
      offerIds,
      paymentMethod,
      serviceMode,
      storeId,
    ] as const,
};

export const useEstimatedPointsEarned = () => {
  const { loyaltyId, loyaltyUserReady, store, serviceMode } = useLoyaltyLegacyStates();
  const { cartEntries } = useCartValues();
  const subtotalAmount = useCartTotal();
  const { checkoutPaymentMethod } = usePaymentContext();
  const appliedOffers = useAppSelector(selectors.loyalty.selectAppliedOffers);

  const storeId = store?.number;
  const paymentMethod = mapPaymentMethodToLoyaltyPaymentMethods(checkoutPaymentMethod);
  const loyaltyServiceMode = LoyaltyServiceMode[serviceMode || ''] || undefined;

  const hasCartEntries = cartEntries.length > 0;
  const entryIds = cartEntries.map(e => `${e.cartId}:${e.quantity}`).join(',');
  const offerIds = appliedOffers.map(o => o.id).join(',');

  const enabled = loyaltyUserReady && !!loyaltyId && hasCartEntries && subtotalAmount > 0;

  const query = useQuery({
    enabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    meta: { persist: false },
    queryKey: estimatedPointsEarnedQueryKeys.params({
      loyaltyId,
      entryIds,
      subtotalAmount,
      offerIds,
      paymentMethod: paymentMethod ?? undefined,
      serviceMode: loyaltyServiceMode,
      storeId: storeId ?? undefined,
    }),
    queryFn: async () => {
      const parsedCartEntries = cartEntries.reduce(parseEntry(), []);

      const appliedIncentives = parseOffers(appliedOffers).map(toLoyaltyPromotionInput);

      const response = await legacyClient.query<
        ILoyaltyUserEstimatedPointsEarnedQuery,
        ILoyaltyUserEstimatedPointsEarnedQueryVariables
      >({
        query: LoyaltyUserEstimatedPointsEarnedDocument,
        variables: {
          loyaltyId: loyaltyId,
          where: {
            cartEntries: parsedCartEntries,
            subtotalAmount,
            ...(appliedIncentives.length > 0 && { appliedIncentives }),
            ...(paymentMethod && { paymentMethod }),
            ...(loyaltyServiceMode && { serviceMode: loyaltyServiceMode }),
            ...(storeId && { storeId }),
          },
        },
        fetchPolicy: 'no-cache',
      });

      if (response.errors) {
        throw response.errors;
      }

      return response.data?.loyaltyUserV2?.estimatedPointsEarned ?? null;
    },
  });

  return { ...query, enabled };
};
